/**
 * LHDN Forms — Borang EA, CP8D, KWSP Form A, PERKESO 8A generator.
 * HR preview on screen + CSV download for LHDN/KWSP/PERKESO portal upload.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FileSignature, Download, Calendar as CalendarIcon, Users, DollarSign, Landmark, Building2, RefreshCw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const CURRENT_MONTH = now.getMonth() + 1;

const FORMS = [
  { id: 'ea-form', title: 'Borang EA', period: 'annual', icon: FileSignature,
    desc: 'Penyata Saraan Tahunan LHDN — satu rekod per staff. Wajib sebelum 28 Feb tahun berikut.',
    download: '/lhdn-forms/ea-form/csv', preview: '/lhdn-forms/ea-form' },
  { id: 'cp8d', title: 'CP8D (PCB Bulanan)', period: 'monthly', icon: DollarSign,
    desc: 'Submisyen PCB bulanan ke LHDN — tempoh dalam 10 hari bulan berikut.',
    download: '/lhdn-forms/cp8d/csv', preview: '/lhdn-forms/cp8d' },
  { id: 'kwsp-form-a', title: 'KWSP Borang A', period: 'monthly', icon: Landmark,
    desc: 'Caruman KWSP bulanan (pekerja + majikan) — tempoh 15hb bulan berikut.',
    download: '/lhdn-forms/kwsp-form-a/csv', preview: '/lhdn-forms/kwsp-form-a' },
  { id: 'perkeso-8a', title: 'PERKESO Borang 8A', period: 'monthly', icon: Building2,
    desc: 'SOCSO + EIS bulanan — tempoh 15hb bulan berikut.',
    download: '/lhdn-forms/perkeso-8a/csv', preview: '/lhdn-forms/perkeso-8a' },
];

const LHDNFormsPage = () => {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState(CURRENT_MONTH);
  const [summary, setSummary] = useState(null);
  const [preview, setPreview] = useState(null);
  const [activeForm, setActiveForm] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/lhdn-forms/summary`, { withCredentials: true })
      .then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const previewForm = async (form) => {
    setLoading(true);
    setActiveForm(form.id);
    try {
      const qs = form.period === 'annual' ? `year=${year}` : `year=${year}&month=${month}`;
      const res = await axios.get(`${API}${form.preview}?${qs}`, { withCredentials: true });
      setPreview({ form, data: res.data });
    } catch (err) {
      alert(err?.response?.data?.detail || 'Ralat');
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = (form) => {
    const qs = form.period === 'annual' ? `year=${year}` : `year=${year}&month=${month}`;
    window.open(`${API}${form.download}?${qs}`, '_blank');
  };

  return (
    <div className="space-y-5" data-testid="lhdn-forms-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-700 to-red-900 rounded-2xl p-5 text-white">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileSignature className="w-7 h-7" />
          Borang Statutori LHDN / KWSP / PERKESO
        </h1>
        <p className="text-red-200 text-sm mt-1">
          Generate borang bulanan & tahunan. Download CSV → upload ke portal rasmi.
        </p>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="lhdn-summary">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-xs text-gray-500">Staff Aktif</p>
            <p className="text-2xl font-bold text-gray-900">{summary.active_staff}</p>
            <p className="text-xs text-gray-400">{summary.current_period}</p>
          </div>
          <div className="bg-red-50 rounded-xl p-4 border border-red-100">
            <p className="text-xs text-red-600">PCB / Bulan</p>
            <p className="text-xl font-bold text-red-700">RM {summary.total_pcb_monthly.toLocaleString()}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
            <p className="text-xs text-emerald-600">EPF / Bulan</p>
            <p className="text-xl font-bold text-emerald-700">RM {summary.total_epf_monthly.toLocaleString()}</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
            <p className="text-xs text-amber-600">SOCSO / Bulan</p>
            <p className="text-xl font-bold text-amber-700">RM {summary.total_socso_monthly.toLocaleString()}</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <p className="text-xs text-blue-600">EIS / Bulan</p>
            <p className="text-xl font-bold text-blue-700">RM {summary.total_eis_monthly.toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Period selector */}
      <div className="bg-white rounded-xl p-4 border border-gray-100 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs font-medium text-gray-500 block mb-1">Tahun</label>
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))}
            min={2020} max={2100}
            data-testid="lhdn-year"
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-32" />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 block mb-1">Bulan (untuk bulanan)</label>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
            data-testid="lhdn-month"
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-40">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{['Jan','Feb','Mac','Apr','Mei','Jun','Jul','Ogo','Sep','Okt','Nov','Dis'][m-1]} {year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Form cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="lhdn-forms-grid">
        {FORMS.map((f) => {
          const Icon = f.icon;
          return (
            <div key={f.id} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm" data-testid={`form-${f.id}`}>
              <div className="flex items-start gap-3">
                <div className="p-3 bg-red-50 rounded-xl">
                  <Icon className="w-5 h-5 text-red-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{f.title}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {f.period === 'annual' ? `Tahunan (${year})` : `Bulanan (${year}-${String(month).padStart(2,'0')})`}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">{f.desc}</p>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => previewForm(f)} disabled={loading && activeForm === f.id}
                      data-testid={`preview-${f.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-200">
                      {loading && activeForm === f.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                      Preview
                    </button>
                    <button onClick={() => downloadCsv(f)}
                      data-testid={`download-${f.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
                      <Download className="w-4 h-4" /> Download CSV
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Preview table */}
      {preview && (
        <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm" data-testid="lhdn-preview">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <CalendarIcon className="w-4 h-4" /> Preview: {preview.form.title}
            </h3>
            <button onClick={() => setPreview(null)} className="text-sm text-gray-500 hover:text-gray-700">Tutup</button>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            {preview.data.period && <>Tempoh: {preview.data.period} • </>}
            Jumlah rekod: {preview.data.count || preview.data.forms?.length || 0}
            {preview.data.total_pcb !== undefined && <> • Total PCB: RM {preview.data.total_pcb.toLocaleString()}</>}
            {preview.data.grand_total !== undefined && <> • Grand Total: RM {preview.data.grand_total.toLocaleString()}</>}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-100">
                  {Object.keys((preview.data.items || preview.data.forms || [{}])[0]).slice(0, 8).map((k) => (
                    <th key={k} className="py-2 pr-3">{k}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(preview.data.items || preview.data.forms || []).slice(0, 50).map((row, idx) => (
                  <tr key={idx} className="border-b border-gray-50">
                    {Object.keys(row).slice(0, 8).map((k) => (
                      <td key={k} className="py-2 pr-3 text-gray-700 truncate max-w-xs">{String(row[k])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {(preview.data.items || preview.data.forms || []).length > 50 && (
              <p className="text-xs text-gray-400 mt-2 text-center">… 50 rekod pertama dipaparkan. Download CSV untuk dataset penuh.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LHDNFormsPage;
